import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { postAuthOtpRequest, postAuthOtpVerify } from '@/api/auth';
import { homeForRole } from '../roles';
import { AuthLink, AuthShell, FormError, deviceInfo, errText, inputCls, startSession } from '../shared';

interface OtpState { identifier?: string; schoolCode?: string }

export function OtpPage() {
  const nav = useNavigate();
  const st = (useLocation().state ?? {}) as OtpState;
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  if (!st.identifier) return <Navigate to="/login" replace />;
  const base = { school_code: st.schoolCode || undefined, identifier: st.identifier, purpose: 'login' };

  const tried = useRef('');
  // Accept a typed code, a pasted code with spaces/dashes, or the whole pasted SMS.
  const takeCode = (raw: string) => {
    const run = raw.match(/\d{6}/)?.[0];
    setCode((run ?? raw.replace(/\D/g, '')).slice(0, 6));
  };

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (busy || code.length !== 6) return;
    tried.current = code;
    setBusy(true); setError(null);
    try {
      const d = (await postAuthOtpVerify({ ...base, code, device: deviceInfo() })).data.data;
      startSession(d);
      nav(d.flags?.must_change_password ? '/change-password' : homeForRole(d.user.role), { replace: true });
    } catch (err) {
      setError(errText(err, 'Verification failed.'));
    } finally { setBusy(false); }
  };

  // Sign in as soon as 6 digits are in (typed, pasted or autofilled from the SMS).
  useEffect(() => { if (code.length === 6 && tried.current !== code) submit(); }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  const resend = async () => {
    setError(null); setInfo(null);
    try { await postAuthOtpRequest(base); setCode(''); tried.current = ''; setInfo('A new code has been sent. Use the newest code. Older ones stop working.'); } catch (err) { setError(errText(err)); }
  };

  return (
    <AuthShell title="Enter your code" subtitle={`We sent a 6-digit code to the contact on ${st.identifier}.`} footer={<AuthLink to="/login">Back to sign in</AuthLink>}>
      <form onSubmit={submit} className="space-y-3">
        <input className={`${inputCls} tracking-[0.5em] text-center text-lg`} inputMode="numeric" autoComplete="one-time-code" value={code}
          onChange={(e) => takeCode(e.target.value)} onPaste={(e) => { e.preventDefault(); takeCode(e.clipboardData.getData('text')); }}
          placeholder="••••••" autoFocus aria-label="One-time code" />
        <FormError text={error} />
        {info && <p className="text-sm text-green-700">{info}</p>}
        <Button type="submit" className="w-full" disabled={busy || code.length !== 6}>{busy ? 'Verifying…' : 'Verify & sign in'}</Button>
        <Button type="button" variant="ghost" className="w-full" onClick={resend}>Resend code</Button>
      </form>
    </AuthShell>
  );
}
